import React from 'react';

function ShieldIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function ServerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
      <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
      <line x1="6" y1="6" x2="6.01" y2="6"/>
      <line x1="6" y1="18" x2="6.01" y2="18"/>
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  );
}

function DatabaseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  );
}

function BotIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2"/>
      <circle cx="12" cy="5" r="2"/>
      <path d="M12 7v4"/>
      <line x1="8" y1="16" x2="8" y2="16"/>
      <line x1="16" y1="16" x2="16" y2="16"/>
    </svg>
  );
}

const SECTIONS = [
  {
    icon: <ShieldIcon />,
    title: 'Data Encryption',
    color: '#3b82f6',
    items: [
      'AES-256 encryption for all data at rest in our Supabase PostgreSQL database',
      'TLS 1.3 for all data in transit — no unencrypted connections accepted',
      'Encryption keys managed by Supabase infrastructure with automatic rotation',
      'Uploaded evidence files encrypted before storage; never accessible without authentication',
    ],
  },
  {
    icon: <ServerIcon />,
    title: 'Infrastructure & Data Residency',
    color: '#10b981',
    items: [
      'Hosted on AWS US-East-1 (N. Virginia) via Supabase and Vercel',
      'All data remains within United States borders — no international transfers',
      'Supabase provides SOC 2 Type II compliant database infrastructure',
      'Vercel provides edge-cached front-end delivery with global DDoS protection',
      'Zero-downtime deployments with automatic rollback on failure',
    ],
  },
  {
    icon: <KeyIcon />,
    title: 'Access Control & Authentication',
    color: '#8b5cf6',
    items: [
      'Multi-factor authentication (MFA) supported and encouraged for all accounts',
      'Role-based access control (RBAC) — users only see their own organization\'s data',
      'Session tokens expire after inactivity and require re-authentication',
      'Complete audit logs of all login attempts and data access events',
      'Auditor access uses time-limited read-only tokens that you control',
    ],
  },
  {
    icon: <DatabaseIcon />,
    title: 'Data Isolation',
    color: '#f59e0b',
    items: [
      'Row-level security (RLS) policies enforced at the database layer — not just application layer',
      'Each organization\'s compliance data is completely isolated from all other tenants',
      'No cross-tenant data access possible even in the event of application vulnerabilities',
      'Assessment answers, evidence files, and reports are private to your account only',
      'Agency sub-accounts can only access client data explicitly shared by the agency admin',
    ],
  },
  {
    icon: <LockIcon />,
    title: 'Backups & Recovery',
    color: '#ef4444',
    items: [
      'Automated daily database backups with 30-day retention period',
      'Point-in-time recovery available — restore to any moment in the past 30 days',
      'Backup data stored in geographically separate AWS region',
      'Recovery time objective (RTO): under 4 hours for full restoration',
      'Business continuity plan and incident response procedures maintained',
    ],
  },
  {
    icon: <BotIcon />,
    title: 'AI Safety & Privacy',
    color: '#06b6d4',
    items: [
      'AI assistant answers compliance framework questions only — not a general-purpose AI',
      'We never instruct the AI to process, store, or analyze your specific organizational data',
      'AI conversations are not used to train AI models',
      'Users are warned not to enter CUI, classified, or sensitive contract data into the AI chat',
      'AI context includes only framework metadata — not your assessment answers or evidence',
      'Enterprise-grade AI processing with data privacy protections',
    ],
  },
];

const CERTIFICATIONS = [
  { name: 'SOC 2 Type I',  status: 'Roadmap', detail: 'Planned Q4 2026',    color: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },
  { name: 'SOC 2 Type II', status: 'Roadmap', detail: 'Planned 2027',       color: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },
  { name: 'ISO 27001',     status: 'Roadmap', detail: 'Planned 2027',       color: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },
  { name: 'FedRAMP Ready', status: 'Roadmap', detail: 'Planned 2028',       color: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },
];

export default function SecurityPage({ onBack }) {
  return (
    <div style={{
      height: '100vh', background: 'var(--bg)',
      fontFamily: 'var(--font)', color: 'var(--text)',
      overflowY: 'auto',
    }}>
      {/* Header */}
      <div className="glass-nav" style={{
        borderRadius: 0,
        padding: '0 40px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
            <rect width="40" height="40" rx="10" fill="url(#secLogoGrad)"/>
            <path d="M20 6L8 11v10c0 7.18 5.13 13.89 12 15.5C27.87 34.89 33 28.18 33 21V11L20 6z"
              fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
            <path d="M14 20.5l4 4 8-8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            <defs>
              <linearGradient id="secLogoGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                <stop stopColor="#2D7DD2"/><stop offset="1" stopColor="#1A4E8A"/>
              </linearGradient>
            </defs>
          </svg>
          <span style={{ fontSize: 15, fontWeight: 600 }}>TheClearAudit</span>
          <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 4 }}>/ Security</span>
        </div>
        {onBack && (
          <span
            onClick={onBack}
            style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Back
          </span>
        )}
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '52px 40px 100px' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.25)',
            borderRadius: 20, padding: '5px 14px', marginBottom: 20,
            fontSize: 11, fontWeight: 600, color: 'var(--accent)',
            fontFamily: 'var(--mono)', letterSpacing: '0.06em',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }}/>
            TRUST CENTER
          </div>
          <h1 style={{
            fontSize: 34, fontWeight: 700, color: 'var(--text)',
            marginBottom: 14, letterSpacing: '-0.02em',
          }}>
            Security & Trust Center
          </h1>
          <p style={{
            fontSize: 15, color: 'var(--text2)', lineHeight: 1.7,
            maxWidth: 580, margin: '0 auto',
          }}>
            TheClearAudit is built for defense contractors who handle sensitive information.
            Here is exactly how we protect your data, our infrastructure, and your compliance posture.
          </p>
        </div>

        {/* CUI Warning */}
        <div style={{
          background: 'var(--amber-bg)', border: '1px solid rgba(245,166,35,0.35)',
          borderRadius: 10, padding: '16px 20px', marginBottom: 44,
          display: 'flex', gap: 14, alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--amber)', marginBottom: 4 }}>
              Important: Do not enter classified or CUI data
            </div>
            <div style={{ fontSize: 12, color: 'var(--amber)', lineHeight: 1.6 }}>
              TheClearAudit is a compliance preparation tool, not a classified system. Do not enter classified information,
              Controlled Unclassified Information (CUI), contract numbers, personnel data, or sensitive technical
              specifications into any part of the platform, including the AI assistant.
            </div>
          </div>
        </div>

        {/* Security Sections */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 44 }}>
          {SECTIONS.map(sec => (
            <div key={sec.title} className="glass-card glass-shine" style={{
              borderRadius: 12, padding: '22px 22px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: `${sec.color}15`, border: `1px solid ${sec.color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: sec.color,
                }}>
                  {sec.icon}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{sec.title}</div>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {sec.items.map((item, i) => (
                  <li key={i} style={{
                    display: 'flex', gap: 8, alignItems: 'flex-start',
                    fontSize: 12, color: 'var(--text2)', marginBottom: 7, lineHeight: 1.55,
                  }}>
                    <span style={{
                      width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                      background: `${sec.color}15`, border: `1px solid ${sec.color}40`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 8, color: sec.color, fontWeight: 700, marginTop: 1,
                    }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="glass-card" style={{
          borderRadius: 12, padding: '24px 24px', marginBottom: 28,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 20 }}>
            Certifications & Compliance Program
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, auto) 1fr',
            gap: '0 16px',
            fontSize: 11, color: 'var(--text3)',
            textTransform: 'uppercase', letterSpacing: '0.07em',
            paddingBottom: 10, borderBottom: '1px solid var(--border)',
            marginBottom: 4,
          }}>
            <div>Certification</div><div>Status</div><div>Timeline</div><div/>
          </div>
          {CERTIFICATIONS.map(c => (
            <div key={c.name} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, auto) 1fr',
              gap: '0 16px',
              padding: '13px 0',
              borderBottom: '1px solid var(--border)',
              alignItems: 'center',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', minWidth: 160 }}>{c.name}</div>
              <div>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                  background: c.bg, color: c.color,
                  border: `1px solid ${c.color}30`,
                }}>
                  {c.status}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', minWidth: 130 }}>{c.detail}</div>
              <div/>
            </div>
          ))}
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>
            Full security documentation, architecture diagrams, and our vendor security questionnaire (VSQ) are available on request.
            Contact <span style={{ color: 'var(--accent)' }}>theclearaudit@gmail.com</span>
          </div>
        </div>

        {/* Responsible Disclosure */}
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '28px 28px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 9, flexShrink: 0,
              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
              Responsible Disclosure Policy
            </div>
          </div>

          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.75, marginBottom: 20 }}>
            We take security seriously. If you discover a vulnerability in TheClearAudit, we ask that you follow responsible disclosure practices and report it to us privately before any public disclosure.
          </div>

          {/* Steps */}
          {[
            {
              num: '1',
              title: 'Report it privately',
              content: (
                <span>
                  Email{' '}
                  <span style={{ color: 'var(--accent)', fontWeight: 600 }}>theclearaudit@gmail.com</span>
                  {' '}with details of the vulnerability. Do not post publicly, create a GitHub issue, or otherwise disclose the vulnerability before we have had a chance to address it.
                </span>
              ),
            },
            {
              num: '2',
              title: 'Include in your report',
              content: (
                <span>
                  A description of the vulnerability and the type of issue · Step-by-step instructions to reproduce the issue · The potential impact or severity of the vulnerability · Any proof-of-concept code or screenshots (if available) · Your contact information (optional — anonymous reports are accepted)
                </span>
              ),
            },
            {
              num: '3',
              title: 'What we commit to',
              content: (
                <span>
                  Acknowledge your report within <strong>48 hours</strong> of receipt · Provide an initial assessment and resolution timeline within <strong>5 business days</strong> · Keep you informed of progress throughout investigation and remediation · Credit you in our security acknowledgments page upon request · Not pursue legal action against researchers who follow this policy in good faith
                </span>
              ),
            },
            {
              num: '4',
              title: 'Scope',
              content: (
                <span>
                  <strong>In scope:</strong> theclearaudit.com, app.theclearaudit.com, api.theclearaudit.com, and all subdomains. Authentication systems, API endpoints, data isolation, and user data handling are all in scope.{' '}
                  <strong>Out of scope:</strong> Third-party services (Stripe, Supabase, Vercel, Anthropic), social engineering attacks, physical security, and issues requiring physical access to systems.
                </span>
              ),
            },
            {
              num: '5',
              title: 'Safe Harbor',
              content: (
                <span>
                  We will not pursue legal action under the Computer Fraud and Abuse Act (CFAA) or similar laws against researchers who discover and report vulnerabilities in good faith, act in accordance with this policy, and do not access, modify, or exfiltrate customer data beyond what is minimally necessary to demonstrate the vulnerability.
                </span>
              ),
            },
          ].map(step => (
            <div key={step.num} style={{
              display: 'flex', gap: 14, marginBottom: 18, alignItems: 'flex-start',
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: 'var(--accent)',
              }}>
                {step.num}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  {step.title}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.65 }}>
                  {step.content}
                </div>
              </div>
            </div>
          ))}

          {/* Report CTA */}
          <div style={{
            marginTop: 20, padding: '14px 18px',
            background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
            borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
                Report a vulnerability
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                We respond within 48 hours and take all reports seriously
              </div>
            </div>
            <a
              href="mailto:theclearaudit@gmail.com?subject=Security%20Vulnerability%20Report&body=Description%3A%0A%0ASteps%20to%20Reproduce%3A%0A%0APotential%20Impact%3A%0A%0AContact%20(optional)%3A"
              style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 6,
                cursor: 'pointer', border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff',
                textDecoration: 'none',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              theclearaudit@gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
